// src/api/companiesApi.ts

import { baseApi } from './baseApi';
import type {
  Company,
  CompaniesResponse,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  ListCompaniesParams,
} from '../types/api';

export const companiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<CompaniesResponse, ListCompaniesParams>({
      query: (params) => ({
        url: '/companies',
        params: {
          ...(params.q && { q: params.q }),
          ...(params.limit && { limit: params.limit }),
          ...(params.offset && { offset: params.offset }),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.companies.map(({ id }) => ({ type: 'Company' as const, id })),
              { type: 'Company', id: 'LIST' },
            ]
          : [{ type: 'Company', id: 'LIST' }],
    }),

    getCompany: builder.query<Company, string>({
      query: (id) => `/companies/${id}`,
      providesTags: (result, error, id) => [{ type: 'Company', id }],
    }),

    createCompany: builder.mutation<Company, CreateCompanyRequest>({
      query: (body) => ({
        url: '/companies',
        method: 'POST',
        body,
      }),
      // Linking contacts on create can set their primary company, which
      // rewrites the company shown in every contact list and summary.
      invalidatesTags: (_r, _e, { contactIds }) =>
        contactIds && contactIds.length > 0
          ? [{ type: 'Company', id: 'LIST' }, 'Contact']
          : [{ type: 'Company', id: 'LIST' }],
    }),

    updateCompany: builder.mutation<Company, { id: string } & UpdateCompanyRequest>({
      query: ({ id, ...body }) => ({
        url: `/companies/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Company', id },
        { type: 'Company', id: 'LIST' },
      ],
    }),

    deleteCompany: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/companies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  // Imperative variant of the same endpoint. `CompanyPicker` needs a read it
  // can await at a specific moment — recovering the canonical id after a 409
  // from a concurrent create — which a declarative hook cannot express.
  useLazyGetCompaniesQuery,
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
} = companiesApi;
